/**
 * React Hook for PWA functionality
 * G2eGente - Marketplace de Recrutamento Ético
 */
import { useState, useEffect, useCallback } from 'react';
import {
  registerServiceWorker,
  initInstallPrompt,
  canInstall,
  promptInstall,
  isAppInstalled,
  isOnline,
  addConnectivityListeners,
  requestNotificationPermission
} from './pwa';

export function usePWA() {
  const [isInstalled, setIsInstalled] = useState(false);
  const [canBeInstalled, setCanBeInstalled] = useState(false);
  const [isOffline, setIsOffline] = useState(!isOnline());
  const [hasUpdate, setHasUpdate] = useState(false);
  const [notificationPermission, setNotificationPermission] = useState('default');

  useEffect(() => {
    // Register service worker
    registerServiceWorker();
    
    // Initialize install prompt handler
    initInstallPrompt();
    
    // Check if already installed
    setIsInstalled(isAppInstalled());
    
    // Check notification permission
    if ('Notification' in window) {
      setNotificationPermission(Notification.permission);
    }

    // Listen for install availability
    const handleInstallAvailable = () => setCanBeInstalled(true);
    const handleInstalled = () => {
      setIsInstalled(true);
      setCanBeInstalled(false);
    };
    const handleUpdateAvailable = () => setHasUpdate(true);

    window.addEventListener('pwa-install-available', handleInstallAvailable);
    window.addEventListener('pwa-installed', handleInstalled);
    window.addEventListener('sw-update-available', handleUpdateAvailable);

    // Listen for connectivity changes
    const removeConnectivityListeners = addConnectivityListeners(
      () => setIsOffline(false),
      () => setIsOffline(true)
    );

    // Check if can install after a delay
    setTimeout(() => {
      setCanBeInstalled(canInstall());
    }, 2000);

    return () => {
      window.removeEventListener('pwa-install-available', handleInstallAvailable);
      window.removeEventListener('pwa-installed', handleInstalled);
      window.removeEventListener('sw-update-available', handleUpdateAvailable);
      removeConnectivityListeners();
    };
  }, []);

  const install = useCallback(async () => {
    const success = await promptInstall();
    if (success) {
      setIsInstalled(true);
      setCanBeInstalled(false);
    }
    return success;
  }, []);

  const requestNotifications = useCallback(async () => {
    const permission = await requestNotificationPermission();
    setNotificationPermission(permission);
    return permission;
  }, []);

  const refresh = useCallback(() => {
    window.location.reload();
  }, []);

  return {
    isInstalled,
    canBeInstalled,
    isOffline,
    hasUpdate,
    notificationPermission,
    install,
    requestNotifications,
    refresh
  };
}

export default usePWA;
